import React, { useEffect, useState, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ExternalLink, BookOpen, Users, TrendingUp, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EditorialLayout } from '../components/EditorialLayout';
const tabs = ['In the Field', 'Substack', 'Deep Dives'];
const updates = [{
  title: 'Scaling Without Shrinking the World',
  summary: 'Natalya Wallin explores how we can scale innovation without depleting the systems we depend on — a case for nature-aligned growth.',
  tag: 'Thought Leadership',
  link: 'https://www.linkedin.com/pulse/scaling-without-shrinking-world-natalya-wallin-ci3bc/?trackingId=Y4e%2Fce8CapwFGB96to571g%3D%3D',
  color: '#edcd99'
}, {
  title: 'Vitality featured in USA Today',
  date: 'November 3, 2024',
  summary: "It's time to rethink what and who we fund, says Vitality founder Natalya Wallin.",
  tag: 'Press',
  link: 'https://www.usatoday.com/story/special/contributor-content/2025/11/03/its-time-to-rethink-what-and-who-we-fund-says-vitality-founder-natalya-wallin/87064743007/',
  color: '#78e1c4'
}, {
  title: 'Vitality Founder Profiled in International Business Times',
  date: 'July 25, 2025',
  summary: "Natalya Wallin discusses the future of biomimicry in venture capital and Vitality's unique thesis.",
  tag: 'Press',
  link: 'https://www.ibtimes.com/vitality-vision-natalya-wallins-full-circle-journey-investing-nature-inspired-innovation-3779469',
  color: '#78e1c4'
}, {
  title: 'Co-hosts first Vital Systems event in NYC',
  date: 'July 22, 2025',
  summary: 'Partnership with GrantMatch, REFASHIOND Ventures, and American Australian Association.',
  tag: 'Event',
  color: '#4DE7D4'
}, {
  title: 'Vitality GP Natalya Wallin interviewed for CEO Weekly',
  date: 'November 2024',
  summary: 'How Natalya Wallin and Vitality are betting on nature to solve modern problems.',
  tag: 'Press',
  link: 'https://ceoweekly.com/the-next-big-wave-in-tech-is-ancient-how-natalya-wallin-and-vitality-are-betting-on-nature-to-solve-modern-problems/',
  color: '#78e1c4'
}, {
  title: 'Emergent Campus Partnership',
  summary: 'Connecting with the Emergent Campus community — a hub for regenerative innovation and interdisciplinary collaboration.',
  tag: 'Partnership',
  link: 'https://emergentcampus.org/about-us/',
  color: '#a0e6e9'
}, {
  title: 'Collaboration with ASU Biomimicry Center',
  date: 'September 23, 2024',
  summary: 'Pilot program supporting faculty and student ventures in biomimicry solutions.',
  tag: 'Partnership',
  color: '#edcd99'
}, {
  title: 'Vitality Invests in ReefCycle',
  date: 'April 21, 2024',
  summary: 'Announcement of investment in nature-based cement startup from Ray of Hope Accelerator.',
  tag: 'Investment',
  color: '#a0e6e9'
}];
const deepDives: Array<{
  slug: string;
  title: string;
  tag: string;
  color: string;
  date: string;
  excerpt: string;
  readTime: string;
}> = [];
const whySubscribeCards = [{
  icon: BookOpen,
  title: 'Original Research',
  description: 'Deep dives into nature-inspired innovation',
  color: '#78e1c4'
}, {
  icon: Users,
  title: 'Founder Spotlights',
  description: 'Conversations with the builders we back',
  color: '#4DE7D4'
}, {
  icon: TrendingUp,
  title: 'Investment Insights',
  description: 'How regenerative systems compound value',
  color: '#a0e6e9'
}];
export function Insights() {
  const [activeTab, setActiveTab] = useState('In the Field');
  // Load Substack embed script
  useEffect(() => {
    if (activeTab === 'Substack') {
      const script = document.createElement('script');
      script.src = 'https://substack.com/embedjs/embed.js';
      script.async = true;
      script.charset = 'utf-8';
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [activeTab]);
  // Neural network nodes
  const nodes = Array.from({
    length: 20
  }, (_, i) => ({
    id: i,
    x: 10 + Math.random() * 80,
    y: 10 + Math.random() * 80,
    size: 0.3 + Math.random() * 0.5,
    pulseDelay: Math.random() * 5,
    pulseDuration: 2 + Math.random() * 2
  }));
  // Generate connections between nearby nodes
  const connections = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[j].x - nodes[i].x;
      const dy = nodes[j].y - nodes[i].y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 25 && Math.random() > 0.5) {
        connections.push({
          id: `${i}-${j}`,
          x1: nodes[i].x,
          y1: nodes[i].y,
          x2: nodes[j].x,
          y2: nodes[j].y,
          delay: Math.random() * 8,
          duration: 3 + Math.random() * 3
        });
      }
    }
  }
  // Raindrops for In the Field tab
  const raindrops = Array.from({
    length: 30
  }, (_, i) => ({
    x: i * 3.3 % 100,
    duration: 3 + Math.random() * 4,
    delay: Math.random() * 8,
    opacity: 0.1 + Math.random() * 0.2,
    width: 1 + Math.random() * 1.5
  }));
  return <div className="min-h-screen pt-20 bg-deepCanopy" data-id="element-1183">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-[#0e2533] via-[#1c3f4d] to-deepCanopy" data-id="element-1184">
        {/* Neural Network Background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40" data-id="element-1185">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-1186">
            <defs data-id="element-1187">
              <filter id="node-glow" data-id="element-1188">
                <feGaussianBlur stdDeviation="0.8" result="coloredBlur" data-id="element-1189" />
                <feMerge data-id="element-1190">
                  <feMergeNode in="coloredBlur" data-id="element-1191" />
                  <feMergeNode in="SourceGraphic" data-id="element-1192" />
                </feMerge>
              </filter>
              <linearGradient id="connection-gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-1193">
                <stop offset="0%" stopColor="#78e1c4" stopOpacity="0" data-id="element-1194" />
                <stop offset="50%" stopColor="#4DE7D4" stopOpacity="1" data-id="element-1195" />
                <stop offset="100%" stopColor="#78e1c4" stopOpacity="0" data-id="element-1196" />
              </linearGradient>
            </defs>

            {connections.map(conn => <motion.line key={conn.id} x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} stroke="url(#connection-gradient)" strokeWidth="0.15" strokeLinecap="round" initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: [0, 1, 0],
            opacity: [0, 0.8, 0]
          }} transition={{
            duration: conn.duration,
            repeat: Infinity,
            delay: conn.delay,
            ease: 'easeInOut'
          }} data-id="element-1197" />)}

            {nodes.map(node => <g key={node.id} data-id="element-1198">
                <motion.circle cx={node.x} cy={node.y} r={node.size} fill="none" stroke="#78e1c4" strokeWidth="0.1" initial={{
              r: node.size,
              opacity: 0
            }} animate={{
              r: [node.size, node.size * 3, node.size],
              opacity: [0, 0.6, 0]
            }} transition={{
              duration: node.pulseDuration,
              repeat: Infinity,
              delay: node.pulseDelay,
              ease: 'easeOut'
            }} data-id="element-1199" />

                <motion.circle cx={node.x} cy={node.y} r={node.size * 0.6} fill="#4DE7D4" filter="url(#node-glow)" initial={{
              opacity: 0.3
            }} animate={{
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.2, 1]
            }} transition={{
              duration: node.pulseDuration,
              repeat: Infinity,
              delay: node.pulseDelay,
              ease: 'easeInOut'
            }} data-id="element-1200" />
              </g>)}
          </svg>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-1201">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight uppercase tracking-wide" data-id="element-1202">
            Insights
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-lg md:text-xl text-vitalityTeal max-w-3xl mx-auto leading-relaxed font-light" data-id="element-1203">
            Press, deep thinking, and data from the field
          </motion.p>
        </div>
      </section>

      {/* Sticky Tab Bar */}
      <div className="sticky top-[72px] z-40 bg-deepCanopy backdrop-blur-md border-b border-white/10" data-id="element-1204">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1205">
          <div className="flex space-x-8 overflow-x-auto no-scrollbar" data-id="element-1206">
            {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative py-4 px-2 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab ? 'text-vitalityTeal' : 'text-gray-400 hover:text-gray-200'}`} data-id="element-1207">
                {tab}
                {activeTab === tab && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-vitalityTeal" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1208" />}
              </button>)}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[50vh] relative" data-id="element-1209">
        <AnimatePresence mode="wait" data-id="element-1210">
          {activeTab === 'In the Field' && <motion.div key="in-the-field" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} className="py-16 relative" data-id="element-1211">
              {/* Subtle Raindrop Background */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20" data-id="element-1212">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" data-id="element-1213">
                  <defs data-id="element-1214">
                    <linearGradient id="raindropGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-1215">
                      <stop offset="0%" stopColor="#78e1c4" stopOpacity="0" data-id="element-1216" />
                      <stop offset="20%" stopColor="#78e1c4" stopOpacity="0.9" data-id="element-1217" />
                      <stop offset="60%" stopColor="#4DE7D4" stopOpacity="0.7" data-id="element-1218" />
                      <stop offset="100%" stopColor="#78e1c4" stopOpacity="0" data-id="element-1219" />
                    </linearGradient>
                  </defs>
                  {raindrops.map((drop, i) => <motion.line key={`rain-${i}`} x1={`${drop.x}%`} y1="0%" x2={`${drop.x}%`} y2="100%" stroke="url(#raindropGradient)" strokeWidth={drop.width} strokeLinecap="round" initial={{
                pathLength: 0,
                opacity: 0
              }} animate={{
                pathLength: [0, 0.4, 0],
                opacity: [0, drop.opacity, 0],
                y: ['0%', '100%']
              }} transition={{
                duration: drop.duration,
                repeat: Infinity,
                ease: 'easeIn',
                delay: drop.delay
              }} data-id="element-1220" />)}
                </svg>
              </div>

              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1221">
                {/* In Action - Professional Engagements */}
                <div className="mb-16" data-id="element-1222">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-1223">
                    In Action
                  </h2>
                  <p className="text-gray-400 mb-8" data-id="element-1224">
                    Speaking, presenting, and building the next generation
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1225">
                    {[{
                  src: 'https://cdn.magicpatterns.com/uploads/9tG6ECG63FkmLaWdBg39pR/Speaking.jpg',
                  caption: 'Speaking at Vital Systems event in New York City',
                  tag: 'NYC Panel'
                }, {
                  src: 'https://cdn.magicpatterns.com/uploads/2yQvqxAAcgq5iq9oidEbTo/biomimicry_slide.jpg',
                  caption: "Presenting on biomimicry and nature's 3.8 billion year R&D lab",
                  tag: 'Keynote'
                }, {
                  src: 'https://cdn.magicpatterns.com/uploads/fqrXvdy8gm6KefinQxY9KQ/Vitality_Duke_students.jpg',
                  caption: 'Hosting the next generation of innovators — Duke University students',
                  tag: 'Mentorship'
                }].map((photo, i) => <motion.div key={photo.tag} initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.15,
                  duration: 0.5
                }} className="group relative rounded-2xl overflow-hidden bg-[#1c3f4d]/40 border border-white/10" data-id="element-1226">
                        <div className="aspect-[4/3] overflow-hidden" data-id="element-1227">
                          <img src={photo.src} alt={photo.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1228" />
                        </div>
                        <div className="p-4 sm:p-5" data-id="element-1229">
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium border uppercase tracking-wider text-vitalityTeal border-vitalityTeal/30 bg-vitalityTeal/10 mb-3" data-id="element-1230">
                            {photo.tag}
                          </span>
                          <p className="text-gray-300 text-sm leading-relaxed" data-id="element-1231">
                            {photo.caption}
                          </p>
                        </div>
                      </motion.div>)}
                  </div>
                </div>

                <EditorialLayout updates={updates} data-id="element-1232" />
              </div>
            </motion.div>}

          {activeTab === 'Substack' && <motion.div key="substack" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} className="py-16" data-id="element-1233">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1234">
                {/* Latest Note — Substack Embed */}
                <div className="flex flex-col items-center mb-16" data-id="element-1235">
                  <div className="flex items-center justify-center gap-3 mb-8" data-id="element-1236">
                    <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-transparent to-vitalityTeal/40" data-id="element-1237" />
                    <span className="text-vitalityTeal text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1238">
                      Latest from the Mycelium
                    </span>
                    <div className="h-px flex-1 max-w-[80px] bg-gradient-to-l from-transparent to-vitalityTeal/40" data-id="element-1239" />
                  </div>

                  <div className="relative" style={{
                maxWidth: '480px',
                width: '100%'
              }} data-id="element-1240">
                    <div className="absolute -inset-3 bg-gradient-to-b from-vitalityTeal/15 via-[#4DE7D4]/5 to-transparent rounded-2xl blur-xl" data-id="element-1241" />

                    <div className="relative bg-[#0e2533]/80 backdrop-blur-md border border-vitalityTeal/20 rounded-xl overflow-hidden shadow-[0_0_40px_rgba(120,225,196,0.08)]" data-id="element-1242">
                      <div className="h-px w-full bg-gradient-to-r from-transparent via-vitalityTeal/60 to-transparent" data-id="element-1243" />

                      <div className="p-2 sm:p-3" data-id="element-1244">
                        <iframe srcDoc={'<html><head><style>{`body{margin:0;padding:0;background:transparent;}.substack-post-embed{width:100%;}`}</style></head><body><div class="substack-post-embed"><p lang="en">Scaling Without Shrinking the World by Vitality VF</p><p>AI, Efficiency, and the Future of Resilient Growth</p><a data-post-link href="https://vitalityvf.substack.com/p/scaling-without-shrinking-the-world">Read on Substack</a></div><script async src="https://substack.com/embedjs/embed.js" charset="utf-8"><' + '/script></body></html>'} width="100%" height="320" frameBorder="0" scrolling="no" className="w-full rounded-md" style={{
                      border: 'none',
                      background: 'transparent'
                    }} title="Scaling Without Shrinking the World — Vitality VF" data-id="element-1245" />
                      </div>

                      <div className="h-px w-full bg-gradient-to-r from-transparent via-vitalityTeal/30 to-transparent" data-id="element-1246" />
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-6 mt-8" data-id="element-1247">
                    <a href="https://substack.com/@vitalityvf" target="_blank" rel="noopener noreferrer" data-id="element-1248">
                      <Button variant="primary" className="group text-sm py-2 px-5" data-id="element-1249">
                        Subscribe
                        <ExternalLink className="w-3.5 h-3.5 ml-1.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-1250" />
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Why Subscribe */}
                <div className="text-center mb-12" data-id="element-1251">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1252">
                    Why Subscribe
                  </h2>
                  <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-1253">
                    Our Substack is where we go deeper — exploring the patterns
                    nature has perfected over billions of years and how they're
                    shaping the next generation of essential technologies.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1254">
                  {whySubscribeCards.map((card, index) => <motion.div key={card.title} initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: index * 0.15
              }} className="relative group" data-id="element-1255">
                      <div className="absolute -inset-2 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  backgroundColor: `${card.color}30`
                }} data-id="element-1256" />

                      <div className="relative bg-[#1c3f4d]/60 backdrop-blur-sm border-2 rounded-2xl p-8 h-full transition-all duration-300 group-hover:border-opacity-100" style={{
                  borderColor: `${card.color}40`
                }} data-id="element-1257">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto" style={{
                    backgroundColor: `${card.color}20`,
                    border: `2px solid ${card.color}60`
                  }} data-id="element-1258">
                          <card.icon className="w-8 h-8" style={{
                      color: card.color
                    }} data-id="element-1259" />
                        </div>

                        <h3 className="text-xl font-bold text-white mb-3 text-center" data-id="element-1260">
                          {card.title}
                        </h3>
                        <p className="text-gray-300 text-center text-sm leading-relaxed" data-id="element-1261">
                          {card.description}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </motion.div>}

          {activeTab === 'Deep Dives' && <motion.div key="deep-dives" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} className="py-16" data-id="element-1262">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1263">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1264">
                  {deepDives.map((post, index) => <motion.div key={post.slug} initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} data-id="element-1265">
                      <Link to={`/insights/deep-dives/${post.slug}`} className="block group h-full" data-id="element-1266">
                        <Card className="h-full p-8 flex flex-col bg-[#1c3f4d]/40 backdrop-blur-sm border-white/10 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg" style={{
                    borderColor: `${post.color}30`
                  }} data-id="element-1267">
                          <div className="flex justify-between items-start mb-6" data-id="element-1268">
                            <span className="px-3 py-1 rounded-full text-xs font-medium border uppercase tracking-wider" style={{
                        color: post.color,
                        borderColor: `${post.color}50`,
                        backgroundColor: `${post.color}10`
                      }} data-id="element-1269">
                              {post.tag}
                            </span>
                            <div className="flex items-center text-gray-400 text-sm" data-id="element-1270">
                              <Clock className="w-4 h-4 mr-1" data-id="element-1271" />
                              {post.readTime}
                            </div>
                          </div>

                          <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-white transition-colors leading-tight" data-id="element-1272">
                            {post.title}
                          </h3>

                          <p className="text-gray-300 leading-relaxed mb-6 flex-grow" data-id="element-1273">
                            {post.excerpt}
                          </p>

                          <div className="flex items-center justify-between mt-auto pt-6 border-t border-white/10" data-id="element-1274">
                            <span className="text-sm text-gray-400" data-id="element-1275">
                              {post.date}
                            </span>
                            <span className="text-sm font-medium flex items-center transition-transform group-hover:translate-x-1" style={{
                        color: post.color
                      }} data-id="element-1276">
                              Read Post <ArrowRight className="w-4 h-4 ml-1" data-id="element-1277" />
                            </span>
                          </div>
                        </Card>
                      </Link>
                    </motion.div>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}